import {
  collection,
  getDocs,
  query,
  where,
  onSnapshot,
  doc,
  getDoc,
} from "firebase/firestore";
import { db } from "../../firebase/config";

export const VERIFICATION_THRESHOLDS = {
  aging: 60,
  stale: 120,
};

const toDate = (value) => {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();

  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const getVerificationMeta = (lastVerified, now = new Date()) => {
  const verifiedDate = toDate(lastVerified);
  const daysSinceVerification = verifiedDate
    ? Math.max(
        0,
        Math.floor((now.getTime() - verifiedDate.getTime()) / (1000 * 60 * 60 * 24))
      )
    : VERIFICATION_THRESHOLDS.stale;

  const verificationStatus =
    daysSinceVerification >= VERIFICATION_THRESHOLDS.stale
      ? "stale"
      : daysSinceVerification >= VERIFICATION_THRESHOLDS.aging
      ? "aging"
      : "fresh";

  return {
    daysSinceVerification,
    verificationStatus,
    needsVerification: verificationStatus !== "fresh",
  };
};

export const normalizeFilterValue = (value) =>
  String(value || "")
    .trim()
    .replace(/\s+/g, " ")
    .toLowerCase();

const createMarkerRecord = (data, id, now = new Date()) => {
  const lastVerified = data.lastVerified || data.approvedAt || data.createdAt;
  const verification = getVerificationMeta(lastVerified, now);

  return {
    ...data,
    id,
    name: data.name || "Unknown Location",
    desc: data.desc || data.description || "",
    lat: data.lat || 0,
    lng: data.lng || 0,
    type: data.type || "unknown",
    area: data.area || "Unknown",
    is_premium: data.premium || false,
    lifecycleStatus: data.status || "active",
    status: verification.verificationStatus,
    verificationStatus: verification.verificationStatus,
    lastVerified,
    daysSinceVerification: verification.daysSinceVerification,
    needsVerification: verification.needsVerification,
    observationCounts: data.observationCounts || {
      buildingStanding: 0,
      exteriorAccess: 0,
      securityPresence: 0,
      maintenanceSigns: 0,
      redevelopmentSigns: 0,
    },
    totalIntelSubmissions: data.totalIntelSubmissions || 0,
    images: data.images && data.images.length > 0 ? data.images : [""],
  };
};

export const containerStyle = {
  width: "100%",
  height: "100%",
};

export const center = { lat: 44.3, lng: -69.8 };

export const mapOptions = {
  backgroundColor: "#000000",
  disableDefaultUI: true,
  zoomControl: false,
  mapTypeControl: false,
  scaleControl: false,
  streetViewControl: false,
  rotateControl: false,
  fullscreenControl: false,
  disableDoubleClickZoom: false,
  gestureHandling: "greedy",
  mapTypeId: "satellite",
  styles: [],
};

export const fetchMarkers = async (user) => {
  try {
    let markersQuery = collection(db, "Markers");

    if (user?.role === "admin") {
      markersQuery = query(markersQuery);
    } else {
      markersQuery = query(markersQuery, where("status", "!=", "pending"));
    }

    const markersSnapshot = await getDocs(markersQuery);
    const now = new Date();
    const markersData = markersSnapshot.docs
      .map((markerDoc) => createMarkerRecord(markerDoc.data(), markerDoc.id, now))
      .filter(
        (marker) =>
          user?.role === "admin" ||
          !["pending", "rejected", "inactive"].includes(marker.lifecycleStatus)
      );

    return markersData;
  } catch (error) {
    console.error("Error fetching markers:", error);
    return [];
  }
};

export const fetchAllMarkers = async () => {
  try {
    const markersQuery = query(
      collection(db, "Markers"),
      where("status", "!=", "pending")
    );

    const markersSnapshot = await getDocs(markersQuery);
    const now = new Date();
    const markersData = markersSnapshot.docs
      .map((markerDoc) => createMarkerRecord(markerDoc.data(), markerDoc.id, now))
      .filter(
        (marker) => !["pending", "rejected", "inactive"].includes(marker.lifecycleStatus)
      );

    return markersData;
  } catch (error) {
    console.error("Error fetching all markers:", error);
    return [];
  }
};

export const subscribeToMarkers = (userOrCallback, maybeCallback) => {
  const user = typeof userOrCallback === "function" ? null : userOrCallback;
  const callback =
    typeof userOrCallback === "function" ? userOrCallback : maybeCallback;

  if (typeof callback !== 'function') {
    console.error('subscribeToMarkers: callback is not a function');
    return () => {};
  }
  
  try {
    const markersQuery =
      user?.role === "admin"
        ? query(collection(db, "Markers"))
        : query(collection(db, "Markers"), where("status", "!=", "pending"));
    return onSnapshot(markersQuery, (snapshot) => {
      const changes = snapshot.docChanges().map((change) => ({
        type: change.type,
        doc: {
          id: change.doc.id,
          ...change.doc.data(),
        },
      }));
      callback(changes);
    });
  } catch (error) {
    console.error("Error setting up marker subscription:", error);
    return () => {};
  }
};

export const getMarkerWithComments = async (markerId) => {
  try {
    const markerDoc = await getDoc(doc(db, "Markers", markerId));
    if (markerDoc.exists()) {
      return createMarkerRecord(markerDoc.data(), markerDoc.id);
    }
    return null;
  } catch (error) {
    console.error("Error fetching marker:", error);
    throw error;
  }
};
